import type { Store } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { logoutSuccess } from "redux-store/features/Auth/Login";
import type { RootState } from "redux-store/store";
import { logger, setTag, setUser } from "services/monitoring";

/**
 * Centralized session expiry handler
 * This ensures consistent behavior across all API layers
 */
class SessionManager {
  private store: Store<RootState> | null = null;
  private isLoggingOut: boolean = false;
  private unsubscribe: (() => void) | null = null;
  private lastIdentityKey: string | null = null;

  /**
   * Initialize with Redux store
   */
  init(store: Store<RootState>): void {
    this.store = store;
    this.updateUserContext();

    // Auth state usually arrives AFTER this runs. init() is called from a mount effect in
    // App.tsx, and OIDC login navigates away and back, so on the callback load the token
    // exchange is still in flight here. Reading the store once left the whole post-login page
    // lifetime reporting to Sentry with no user at all - 46% of info events, 48% of ui.screen
    // spans and 100% of pageload spans were unattributed on the first production day (NKF-1355).
    this.unsubscribe?.();
    this.unsubscribe = store.subscribe(() => this.updateUserContext());
  }

  /**
   * Stop tracking auth changes. Only needed by tests and hot reload; the app calls init() once.
   */
  destroy(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.store = null;
    this.lastIdentityKey = null;
  }

  /**
   * Update user context in monitoring.
   *
   * Runs on every dispatch, so it exits early unless the identity itself changed.
   */
  private updateUserContext(): void {
    if (!this.store) return;

    const state = this.store.getState();
    const auth = state?.auth;
    const isAuthenticated = Boolean(auth?.loggedIn && auth?.isAuthorized);

    // account_id is part of the key because a changed active account is a changed identity for
    // tagging purposes, even though the person is the same.
    const identityKey = isAuthenticated ? `${auth?.userId}:${auth?.account_id}` : null;
    if (identityKey === this.lastIdentityKey) return;
    this.lastIdentityKey = identityKey;

    if (isAuthenticated) {
      // id is the person. It used to be auth.account_id - the active_account FK, shared by every
      // user in a practice - which made Sentry's count_unique(user) and every issue's "users
      // affected" a count of practices. The account keeps its own tag.
      setUser({
        id: auth?.userId,
        email: auth?.email,
        username: auth?.username,
      });
      setTag("account_id", auth?.account_id ?? undefined);
    } else {
      setUser(null);
      setTag("account_id", undefined);
    }
  }

  /**
   * Check if response indicates session expiry
   */
  isSessionExpired(error: any): boolean {
    const status = error?.response?.status || error?.status;
    return status === 401 || status === 403;
  }

  /**
   * Handle session expiry - logout user and show notification
   */
  handleSessionExpiry(source: string = "API"): void {
    if (this.isLoggingOut || !this.store) {
      return;
    }

    const state = this.store.getState();
    const isLoggedIn = state?.auth?.loggedIn;

    // Only proceed if user is currently logged in
    if (!isLoggedIn) {
      return;
    }

    // Stable message, variable in an attribute: this is 84% of our prod log volume, and as a
    // formatted string the only thing you could do with it was count it. As a log with `source`
    // it groups by which path noticed (Redux API vs React Query), and it inherits the user and
    // account_id that sessionManager itself sets, so "which providers, which practice, how
    // often" is a query rather than an investigation.
    logger.warn("auth.session_expired", { source });

    this.isLoggingOut = true;

    // Show session expiry notification
    toast.error("Session expired. Please log in again.");

    // Dispatch logout action
    this.store.dispatch(logoutSuccess());

    // Clear user context. The subscription above already reacts to the dispatch; this keeps the
    // clear working if init() was never called, and routes it through the one place that owns
    // the Sentry identity.
    this.updateUserContext();

    // Reset flag after a short delay
    setTimeout(() => {
      this.isLoggingOut = false;
    }, 1000);
  }

  /**
   * Check if user is currently logged in
   */
  isAuthenticated(): boolean {
    if (!this.store) return false;
    const state = this.store.getState();
    return state?.auth?.loggedIn && state?.auth?.isAuthorized;
  }

  /**
   * Get current user's account ID
   */
  getAccountId(): string | null {
    if (!this.store) return null;
    const state = this.store.getState();
    return state?.auth?.account_id;
  }
}

// Export singleton instance
export const sessionManager = new SessionManager();
